import { CrudForm } from '../shared/CrudPage';
import { enumEntries, stockMovementService } from '@kss/api';
import type { StockMovement, StockMovementCreate, StockMovementFilter } from '@kss/api';
import { loadStoreOptions, loadWarehouseOptions, loadProductOptions } from '../shared/formLoaders';

/**
 * StockMovement form — wired to the live schema verified
 * 2026-05-05. Live row uses `movementDate` (not `movedAt`),
 * `referenceType` + free-text `notes` (no separate `reference`
 * column), and `movementType` is an int enum (1=In, 2=Out,
 * 3=Transfer, 4=Adjustment). Strict-mode binder rejects the legacy
 * keys with `UNKNOWN_FIELD`.
 */

/* Single source of truth — mirrors the LIVE backend constant
   `Library/Common/Inventory/MovementType.cs` (In 1, Out 2, Transfer 3,
   Reserve 4, Release 5). The old local map called 4 "Adjustment", so
   backend-emitted soft-hold rows displayed under the wrong name. Manual
   stock corrections belong to the Stock Adjustment module. */
const TYPE_BY_LABEL: Record<string, number> = Object.fromEntries(
  enumEntries('StockMovementType').map((e) => [e.label, e.value]),
);
const LABEL_BY_TYPE: Record<number, string> = Object.fromEntries(
  Object.entries(TYPE_BY_LABEL).map(([k, v]) => [v, k]),
);

type F = {
  productId: string;
  storeId: string;
  warehouseId: string;
  movementType: string;
  quantity: number;
  unitCost: number;
  referenceType: string;
  movementDate: string;
  notes: string;
};
const today = new Date().toISOString().slice(0, 10);
const empty: F = {
  productId: '',
  storeId: '',
  warehouseId: '',
  movementType: 'In',
  quantity: 0,
  unitCost: 0,
  referenceType: '',
  movementDate: today,
  notes: '',
};

export default function NewStockMovement() {
  return (
    <CrudForm<StockMovement, StockMovementCreate, StockMovementFilter, F>
      title="stock movement"
      service={stockMovementService}
      idKey="stockMovementId"
      basePath="/panel/stock-movements"
      emptyForm={empty}
      fields={[
        { key: 'productId', label: 'Product', type: 'search', loadOptions: loadProductOptions, required: true, placeholder: 'Pick a product' },
        { key: 'movementType', label: 'Type', type: 'select', options: Object.keys(TYPE_BY_LABEL).map((v) => ({ value: v, label: v })) },
        { key: 'quantity', label: 'Quantity', type: 'number', required: true },
        { key: 'unitCost', label: 'Unit cost (₹)', type: 'number' },
        { key: 'movementDate', label: 'Date', type: 'date' },
        { key: 'storeId', label: 'Store', type: 'search', loadOptions: loadStoreOptions, placeholder: 'Select a store' },
        { key: 'warehouseId', label: 'Warehouse', type: 'search', loadOptions: loadWarehouseOptions, placeholder: 'Select a warehouse' },
        {
          key: 'referenceType',
          label: 'Reference type',
          placeholder: 'PurchaseOrder / SalesOrder / Adjustment',
        },
        { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        productId: r.productId ?? '',
        storeId: r.storeId ?? '',
        warehouseId: r.warehouseId ?? '',
        movementType:
          typeof r.movementType === 'number'
            ? LABEL_BY_TYPE[r.movementType] ?? 'In'
            : String(r.movementType ?? 'In'),
        quantity: Number(r.quantity ?? 0),
        unitCost: Number(r.unitCost ?? 0),
        // Live `referenceType` first; fall back to legacy `reference`.
        referenceType: r.referenceType ?? r.reference ?? '',
        movementDate: ((r.movementDate ?? r.movedAt) ?? `${today}T00:00:00`).slice(0, 10),
        notes: r.notes ?? '',
      })}
      toEntity={(f, id) => ({
        stockMovementId: id,
        productId: f.productId,
        storeId: f.storeId || undefined,
        warehouseId: f.warehouseId,
        movementType: TYPE_BY_LABEL[f.movementType] ?? 1,
        quantity: f.quantity,
        unitCost: f.unitCost || undefined,
        referenceType: f.referenceType || undefined,
        movementDate: f.movementDate ? `${f.movementDate}T00:00:00` : undefined,
        notes: f.notes || undefined,
      })}
      validate={(f) => !f.productId.trim() ? 'Product is required' : (f.quantity <= 0 ? 'Quantity must be > 0' : null)}
    />
  );
}
